<template>
  <perfect-scrollbar>
    <ul class="flex flex-col gap-4">
      <li v-for="(idx, i) in indexes" :key="i"
          class="flex flex-col border border-solid border-gray-border rounded"
          :class="{ 'bg-primary-light': !isSelectedIndex(i) && hoverI === i }"
          @mouseenter="hoverI = i"
          @mouseleave="hoverI = null"
      >
        <div class="flex items-center justify-between cursor-pointer pr-2"
             :class="[
              { 'border-0 border-b border-solid border-soft': isSelectedIndex(i) },
              { 'bg-primary-light': isSelectedIndex(i) && selectedIndexSourcePosition === -1 }
             ]"
        >
          <input-with-error-message v-if="i === renameIndexPosition"
                                    id="indexes-list-name-input"
                                    :value.sync="indexName"
                                    :is-loading="isIndexNameLoading"
                                    @escape="setRenameIndex()"
                                    @submit="renameIndex()"
                                    class="w-full py-4 pl-8"
          />
          <router-link v-else
                       :to="getIndexRoute(idx.id)"
                       class="w-11/12 grid grid-cols-10 py-6"
                       @click.native="handleIndexNameClick(i)"
          >
            <div class="col-span-5 flex items-center">
              <vs-icon :icon="isSelectedIndex(i) ? 'arrow_drop_down' : 'arrow_right'" class="text-gray-dark w-8" size="1.2rem"/>
              <span class="text-black font-semibold">{{ idx.name }}</span>
            </div>

            <div class="col-span-1 col-start-6 flex justify-between">
              <index-execution-status-icon :index="idx" :index-position="i"/>
              <index-execution-next-update v-if="idx.schedule_params"
                                           :cron-expression="getCronExpression(idx.schedule_params)"
                                           :icon-class="'w-4'"
              />
            </div>
            <div class="flex items-center col-span-4 pl-3">
              <index-execution-last-update v-if="hasLastIndexExecution(idx)"
                                           :index-execution="idx.last_index_execution"
              />
              <index-execution-warning v-else :has-schedule-params="!!idx.schedule_params"/>
            </div>
          </router-link>

          <side-list-menu v-if="i !== renameIndexPosition"
                          :items="menuItems"
                          @rename="setRenameIndex(i, idx.name)"
                          @reindex-all-sources="$emit('start-reindex', idx)"
                          @delete="$emit('request-delete', idx)"
                          class=""
          />
        </div>

        <div v-if="isSelectedIndex(i)">
          <div class="flex justify-between items-center pl-8 pr-4 py-3">
            <span class="text-gray-dark">{{ sourcesSummaryText }}</span>
            <settings-indexes-add-source-button :is-adding="isAddingNewIndexSource"
                                         @add-source="handleAddIndexSourceClick"
            />
          </div>

          <div v-for="(idxSource, j) in indexSources" :key="j"
               class="flex justify-between items-center py-3 pr-2 border-0 border-t border-solid border-soft cursor-pointer"
               :class="{ 'bg-primary-light': isSelectedIndexSource(j) || hoverJ === j }"
               @mouseenter="hoverJ = j"
               @mouseleave="hoverJ = null"
          >
            <input-with-error-message v-if="j === renameIndexSourcePosition"
                                      id="index-source-name-input"
                                      :value.sync="indexSourceName"
                                      :is-loading="isIndexSourceNameLoading"
                                      @escape="setRenameIndexSource()"
                                      @submit="renameIndexSourceName()"
                                      class="w-full py-3 pl-8"
            />
            <div v-else class="w-11/12" @click="handleIndexSourceClick(j)">
              <div class="grid grid-cols-10">
                <span class="col-span-5 pl-8 text-black truncate pr-2">{{ idxSource.name }}</span>
                <div class="col-span-5 text-gray-dark flex items-center gap-2">
                  <component
                    :is="getIndexSourceError(idxSource) ? 'vx-tooltip' : 'div'"
                    :text="getIndexSourceError(idxSource)"
                    class="flex items-center gap-2"
                  >
                    <img :src="getIndexSourceIcon(idxSource.type)" alt="index source icon" class=""/>
                    <span>{{ renderFormatText(idxSource.format, idxSource.type) }}</span>
                    <span v-if="getIndexSourceError(idxSource)">⚠️</span>
                  </component>
                </div>
              </div>
            </div>
            <side-list-menu v-if="j !== renameIndexSourcePosition"
                            :items="getIndexSourceMenuItems(idx)"
                            @rename="setRenameIndexSource(j, idxSource.name)"
                            @reindex-all-sources="$emit('start-reindex', idx)"
                            @delete="$emit('delete-index-source', idxSource)"
            />
          </div>
        </div>
      </li>
    </ul>
  </perfect-scrollbar>
</template>

<script>
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SideListMenu from '@/components/common/SideListMenu'
import IndexExecutionStatusIcon from '@/components/settings/indexes/IndexExecutionStatusIcon.vue'
import IndexExecutionLastUpdate from '@/components/settings/indexes/IndexExecutionLastUpdate.vue'
import IndexExecutionNextUpdate from '@/components/settings/indexes/IndexExecutionNextUpdate.vue'
import IndexExecutionWarning from '@/components/settings/indexes/IndexExecutionWarning.vue'
import { ROUTE_NAMES } from '@/router'
import { MENU_ITEMS } from '@/utils/editor.constants'
import SettingsIndexesAddSourceButton from '@/components/settings/indexes/SettingsIndexesAddSourceButton.vue'
import scrape_web_icon from '@/assets/images/icons/scrape_web_icon.svg'
import code_file_icon from '@/assets/images/icons/code_file_icon.svg'
import file_icon from '@/assets/images/icons/file_icon.svg'

export default {
  name: 'SettingsIndexesList',
  components: {
    SettingsIndexesAddSourceButton,
    PerfectScrollbar,
    SideListMenu,
    IndexExecutionStatusIcon,
    IndexExecutionLastUpdate,
    IndexExecutionNextUpdate,
    IndexExecutionWarning,
    InputWithErrorMessage
  },
  props: {
    selectedIndexPosition: {
      type: Number,
      default: 0
    },
    selectedIndexSourcePosition: {
      type: Number,
      default: -1
    },
    indexes: {
      type: Array,
      required: true
    },
    isIndexNameLoading: {
      type: Boolean,
      default: false
    },
    isIndexSourceNameLoading: {
      type: Boolean,
      default: false
    },
    isAddingNewIndexSource: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    isIndexNameLoading: {
      immediate: false,
      handler(isIndexNameLoading) {
        if(!isIndexNameLoading) {
          this.setRenameIndex()
        }
      }
    },
    isIndexSourceNameLoading: {
      immediate: false,
      handler(isIndexSourceNameLoading) {
        if(!isIndexSourceNameLoading) {
          this.setRenameIndexSource()
        }
      }
    }
  },

  data() {
    return {
      hoverI: null,
      hoverJ: null,
      renameIndexPosition: -1,
      renameIndexSourcePosition: -1,
      indexName: '',
      indexSourceName: ''
    }
  },

  computed: {
    menuItems() {
      return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllSources, MENU_ITEMS.delete]
    },
    routeData() {
      return this.indexes.map(index => ({ name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: index.id } }))
    },
    selectedIndex() {
      return this.indexes[this.selectedIndexPosition]
    },
    indexSources() {
      return this.selectedIndex?.sources
    },
    sourcesSummaryText() {
      if (!this.indexSources) return null

      const sourcesLength = this.indexSources.length
      return `${sourcesLength} ${sourcesLength > 1 ? 'sources' : 'source'}`
    }
  },

  methods: {
    getIndexRoute(indexId) {
      return { name: ROUTE_NAMES.knowledgeBases, params: { knowledgeBaseId: indexId } }
    },
    getIndexSourceMenuItems(index) {
      const menuItemReindex = { ...MENU_ITEMS.reindexAllSources, label: 'Reindex' }
      if (index.sources.length === 1) return [MENU_ITEMS.rename, menuItemReindex]

      const menuItemDelete = { ...MENU_ITEMS.delete, label: 'Delete source' }
      return [MENU_ITEMS.rename, menuItemReindex, menuItemDelete]
    },
    getCronExpression(scheduleParams) {
      if (!scheduleParams) return null

      return scheduleParams.cronExpression
    },
    handleAddIndexSourceClick({ el, sourceType }) {
      this.$emit('show-add-index-source', {
        isAdding: !this.isAddingNewIndexSource,
        sourceType
      })
      el.blur()
    },
    handleIndexNameClick(indexPosition) {
      if (indexPosition === this.selectedIndexPosition) {
        this.updateIndexSourcePosition(-1)
      }
    },
    handleIndexSourceClick(indexSourcePosition) {
      if (this.selectedIndexSourcePosition !== -1 && indexSourcePosition === this.selectedIndexSourcePosition) {
        this.updateIndexSourcePosition(-1)
      } else {
        this.updateIndexSourcePosition(indexSourcePosition)
      }
    },
    hasLastIndexExecution(index) {
      return !!index.last_index_execution?.created_on
    },
    updateIndexSourcePosition(indexSourcePosition) {
      this.$emit('update-index-source-position', indexSourcePosition)
    },
    setRenameIndex(indexPosition = -1, indexName = '') {
      this.renameIndexPosition = indexPosition
      this.indexName = indexName
    },
    setRenameIndexSource(indexSourcePosition = -1, indexSourceName = '') {
      this.renameIndexSourcePosition = indexSourcePosition
      this.indexSourceName = indexSourceName
    },
    renameIndex() {
      if (this.renameIndexPosition === -1) return

      const index = this.indexes[this.renameIndexPosition]
      if (index.name === this.indexName) {
        this.setRenameIndex()
        return
      }

      this.$emit('rename', { ...index, name: this.indexName }, this.renameIndexPosition)
    },
    renameIndexSourceName() {
      if (this.renameIndexSourcePosition === -1) return

      const selectedIndex = this.indexes[this.selectedIndexPosition]
      const indexSource = selectedIndex.sources[this.renameIndexSourcePosition]
      if (indexSource.name === this.indexSourceName) {
        this.setRenameIndexSource()
        return
      }

      this.$emit('rename-index-source', { ...indexSource, name: this.indexSourceName })
    },
    isSelectedIndex(indexPosition) {
      return indexPosition === this.selectedIndexPosition
    },
    isSelectedIndexSource(indexPosition) {
      return indexPosition === this.selectedIndexSourcePosition
    },
    renderFormatText(format, type) {
      if (type === 'web_scraping') return 'Web Page'
      if (type === 'url') return format === 'xml' ? 'XML Feed' : 'JSON Feed'
      return format.toUpperCase() + ' File'
    },
    getIndexSourceIcon(indexSourceType) {
      if (indexSourceType === 'file') return file_icon
      if (indexSourceType === 'url') return code_file_icon
      return scrape_web_icon
    },
    getIndexSourceError(indexSource) {
      return indexSource.config?.error
    }
  }
}
</script>
